import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  Box,
  IconButton,
  Popover,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import { Send, Mic, Image as ImageIcon, AddShoppingCart, PlaceRounded } from "@mui/icons-material";
import { isWhatsappAcceptedImageUrl } from "../../utils/whatsappProductImage";
import { useUsers } from "../../hooks/useUsers";
import { useAgents } from "../../hooks/useAgents";
import MicRecorder from "mic-recorder-to-mp3";
import ChannelSelector, {
  INTERNAL_NOTE_CHANNEL_ID,
  PERSONAL_WHATSAPP_CHANNEL_ID,
} from "./ChannelSelector";
import MessageInputField from "./MessageInputField";
import FileDropHandler from "./FileDropHandler";
import FilePreview from "./FilePreview";
import AudioPreview from "./AudioPreview";
import RecordingIndicator from "./RecordingIndicator";
import EmojiPicker from "./EmojiPicker";
import { COMPOSER_ROW_HEIGHT_PX } from "./composerConstants";
import { useMediaQuery } from "@mui/material";
import { getChannelTypeById, getChannelById } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import MessagePhoneChannel from "./MessagePhoneChannel";
import MessageWhatsAppChannel from "./MessageWhatsAppChannel";
import MessageEmailChannel from "./MessageEmailChannel";
import MessageMessengerChannel from "./MessageMessengerChannel";
import MessageSMSChannel from "./MessageSMSChannel";
import MessageAppChannel from "./MessageAppChannel";
import MessageWordpressChannel from "./MessageWordpressChannel";
import { composeChannelFormatOptions } from "./composeFormatOptions";
import {
  generateSuggestedReply,
  getConversationMessageSuggestions,
  fetchData,
  putConversationCart,
} from "../../api/client";
import { useProducts } from "../../hooks/useProducts";
import { isWhatsAppQrChannelTipo, CHANNEL_TYPES } from "../../constants/channelCatalog";
import { isLikelyWhatsappGroupJid } from "../../utils/whatsappJid";
import { useTranslation } from "react-i18next";
import {
  channelTypeRequiresClientPhone,
  hasClientPhoneRegistered,
} from "../../utils/clientContactUtils";
import { buildStaticMapPreviewUrl } from "../../utils/locationMapPreview";
import { useAuthContext } from "../../context/AuthContext";

const formatOptions = composeChannelFormatOptions;

const EXTERNAL_CLIENT_BANNER_DONE_KEY = (conversationId) =>
  conversationId != null ? `xpro_extClientBannerDone_${conversationId}` : null;

/** Tipos “default” (0) não têm garantia de envio ao cliente — não exibir aviso de envio externo. */
function isClientOutboundChannelTipo(tipo) {
  if (tipo == null || tipo === "" || Number.isNaN(Number(tipo))) return false;
  return Number(tipo) !== CHANNEL_TYPES.DEFAULT;
}

function hasMyExternalOutboundMessage(messagesList, myUserId) {
  if (myUserId == null) return false;
  const me = Number(myUserId);
  if (!Number.isFinite(me)) return false;
  const list = Array.isArray(messagesList) ? messagesList : [];
  return list.some((m) => {
    const typ = String(m?.type || "").toLowerCase();
    if (typ === "note" || typ === "log" || typ === "xpro") return false;
    const st = String(m?.senderType || m?.sender || "").toLowerCase();
    if (!["agent", "operator", "user", "chatbot", "ai_agent"].includes(st)) return false;
    const uid = Number(m?.userId ?? m?.id_usuario ?? m?.senderUserId ?? 0);
    return uid === me && uid !== 0;
  });
}

const isChatBotUser = (u) => Number(u?.ura) === 1;

function catalogCoverUrl(product) {
  const g = product?.galeria;
  if (!Array.isArray(g) || !g.length) return null;
  const c = g.find((x) => x?.isCover) || g[0];
  const u = c?.url || c?.src;
  return u && String(u).trim() ? String(u).trim() : null;
}

const MessageInput = ({
  onSendMessage,
  selectedConversation,
  messages: messagesIn = [],
  hasHistory = false,
  onRefetchConversationDetails,
  /** Notifica o canal efetivo do seletor (envio) — ex.: barra de voz deve seguir este id, não só o da solicitação. */
  onComposeChannelChange,
}) => {
  const messages = Array.isArray(messagesIn) ? messagesIn : [];
  const { userData } = useAuthContext();
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const { t } = useTranslation("chat");
  const [phoneBlockSnackbarOpen, setPhoneBlockSnackbarOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(() => {
    if (selectedConversation?.kind === "personal") return PERSONAL_WHATSAPP_CHANNEL_ID;
    return (
      sessionStorage.getItem(`selectedChannel${selectedConversation?.id}`) ||
      selectedConversation?.channelId
    );
  });
  const [message, setMessage] = useState("");
  const messageText = typeof message === "string" ? message : "";
  const [files, setFiles] = useState([]);
  const [audioURL, setAudioURL] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mp3RecorderRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const discardRequestedRef = useRef(false);
  const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
  const fileInputRef = useRef(null);
  const [origin, setOrigin] = useState("computer");
  const [destination, setDestination] = useState("(21) 99876-5432");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isGeneratingSuggestion, setIsGeneratingSuggestion] = useState(false);
  const [suggestions, setSuggestions] = useState({ templates: [], quickReplies: [] });
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [transferTo, setTransferTo] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const mentionAnchorRef = useRef(null);
  const suggestionExtrasRef = useRef({ focusProductIds: [], focusLineItemIds: [] });
  const [mentionHighlightIndex, setMentionHighlightIndex] = useState(0);
  const [productHighlightIndex, setProductHighlightIndex] = useState(0);
  const [mentionOverlayPosition, setMentionOverlayPosition] = useState(null);
  const [cartSaving, setCartSaving] = useState(false);
  const [locationSending, setLocationSending] = useState(false);
  const [locationConfirmOpen, setLocationConfirmOpen] = useState(false);
  const [locationDraft, setLocationDraft] = useState(null);
  const [locationConfirmMapFailed, setLocationConfirmMapFailed] = useState(false);
  const [composeRuleSnackbarOpen, setComposeRuleSnackbarOpen] = useState(false);
  const [composeRuleKey, setComposeRuleKey] = useState(null);
  const [mentionConfirmOpen, setMentionConfirmOpen] = useState(false);

  useEffect(() => {
    const handler = (ev) => {
      const d = ev.detail || {};
      if (d.conversationId != null && Number(d.conversationId) !== Number(selectedConversation?.id)) {
        return;
      }
      if (d.appendText) {
        const t = String(d.appendText);
        setMessage((m) => (m && m.trim() ? `${m.trim()}\n\n${t}` : t));
      }
      if (d.imageUrl && /^https?:\/\//i.test(String(d.imageUrl))) {
        const url = String(d.imageUrl);
        (async () => {
          const blobToFile = (blob) => {
            const ext =
              (blob.type || "").includes("png") ? "png" : (blob.type || "").includes("webp") ? "webp" : "jpeg";
            return new File([blob], `imagem.${ext}`, { type: blob.type || "image/jpeg" });
          };
          try {
            let blob;
            try {
              const r = await fetch(url, { mode: "cors" });
              if (!r.ok) throw new Error(`HTTP ${r.status}`);
              blob = await r.blob();
            } catch {
              // Loja/CDN sem CORS: o backend já baixa e envia ao S3 (mesmo fluxo da importação de produtos).
              const resp = await fetchData("/uploads/fetch", "POST", {
                url,
                prefix: "chat-compose",
                filename: "imagem",
              });
              const proxied = resp?.data?.url;
              if (!proxied) throw new Error("proxy sem url");
              const r2 = await fetch(proxied, { mode: "cors" });
              if (!r2.ok) throw new Error(`S3 ${r2.status}`);
              blob = await r2.blob();
            }
            setFiles([blobToFile(blob)]);
            setAudioURL(null);
          } catch {
            // eslint-disable-next-line no-alert
            alert("Não foi possível baixar a imagem (CORS). O link foi colocado no texto.");
            setMessage((m) => (m && m.trim() ? `${m.trim()}\n${url}` : url));
          }
        })();
      }
      if (Array.isArray(d.focusProductIds) && d.focusProductIds.length) {
        suggestionExtrasRef.current.focusProductIds = d.focusProductIds
          .map((x) => Number(x))
          .filter((n) => n > 0);
      }
      if (Array.isArray(d.focusLineItemIds) && d.focusLineItemIds.length) {
        suggestionExtrasRef.current.focusLineItemIds = d.focusLineItemIds
          .map((x) => Number(x))
          .filter((n) => n > 0);
      }
    };
    window.addEventListener("xpro-chat-compose", handler);
    return () => window.removeEventListener("xpro-chat-compose", handler);
  }, [selectedConversation?.id]);

  // Precisa incluir ChatBots (ura=1) para o @/transferência permitir direcionar ao bot.
  const { data: usersResp } = useUsers(1, 150, "", "todos");
  const { data: agentsResp } = useAgents(1, 200, "");
  const users = usersResp?.users ?? [];
  const agents = agentsResp?.agents ?? [];
  const humanUsers = (users || []).filter((u) => !isChatBotUser(u));
  const chatbotUsers = (users || []).filter(isChatBotUser);

  const lastAt = messageText.lastIndexOf("@");
  const lastHash = messageText.lastIndexOf("#");
  const mentionQuery = lastAt >= 0 ? messageText.slice(lastAt + 1) : "";
  const productQuery = lastHash >= 0 ? messageText.slice(lastHash + 1) : "";
  const atMentionToken = lastAt >= 0 && !/\s/.test(mentionQuery);
  const hashProductToken = lastHash >= 0 && !/\s/.test(productQuery);
  /** Qual autocompletar vale: o gatilho mais à direita na mensagem ganha (igual fluxo @ vs #). */
  const mentionPickerActive = atMentionToken && (!hashProductToken || lastAt > lastHash);
  const productPickerActive = hashProductToken && (!atMentionToken || lastHash > lastAt);

  const mentionQueryLower = (mentionQuery || "").toLowerCase();
  const productQueryLower = (productQuery || "").toLowerCase();

  const cartProducts = useMemo(() => {
    const raw = selectedConversation?.products;
    if (!Array.isArray(raw)) return [];
    return raw
      .map((p) => ({
        id: p.id,
        productId: p.productId ?? p.product_id ?? null,
        nome: p.nome ?? p.name ?? "",
        quantidade: p.quantidade ?? p.quantity ?? 1,
        valor: p.valor ?? p.value ?? 0,
        coverImageUrl: p.coverImageUrl ?? null,
        sku: p.sku != null ? String(p.sku) : null,
      }))
      .filter((p) => p.nome);
  }, [selectedConversation?.id, selectedConversation?.products]);

  const projectId = selectedConversation?.projectId;
  const productParams = projectId ? { ids_projetos: String(projectId) } : {};
  const catalogEnabled = productPickerActive && Boolean(projectId);
  const { data: catalogData, isLoading: catalogLoading } = useProducts(
    1,
    500,
    productQuery,
    productParams,
    { enabled: catalogEnabled }
  );

  const productOptions = useMemo(() => {
    const matchText = (nome, sku) => {
      if (!productQueryLower) return true;
      const n = (nome || "").toLowerCase();
      const s = sku ? String(sku).toLowerCase() : "";
      return n.includes(productQueryLower) || s.includes(productQueryLower);
    };

    const cartFiltered = cartProducts.filter((line) => matchText(line.nome, line.sku));
    const cartProductIds = new Set(
      cartFiltered.map((c) => c.productId).filter((id) => id != null && !Number.isNaN(Number(id)))
    );

    const catalogList = catalogEnabled ? catalogData?.products ?? [] : [];
    const catalogExtra = catalogList
      .filter((cat) => matchText(cat.nome, cat.sku))
      .filter((cat) => !cartProductIds.has(cat.id))
      .map((cat) => ({
        rowKey: `cat-${cat.id}`,
        inCart: false,
        lineId: null,
        productId: cat.id,
        nome: cat.nome || "",
        quantidade: 1,
        valor: cat.preco != null ? Number(cat.preco) : 0,
        coverImageUrl: catalogCoverUrl(cat),
        sku: cat.sku != null ? String(cat.sku) : null,
      }));

    const cartRows = cartFiltered.map((line) => ({
      rowKey: `line-${line.id}`,
      inCart: true,
      lineId: line.id,
      productId: line.productId,
      nome: line.nome,
      quantidade: line.quantidade,
      valor: line.valor,
      coverImageUrl: line.coverImageUrl,
      sku: line.sku,
    }));

    const byName = (a, b) => (a.nome || "").localeCompare(b.nome || "", "pt");
    return [...cartRows.sort(byName), ...catalogExtra.sort(byName)];
  }, [cartProducts, catalogData?.products, catalogEnabled, productQueryLower]);

  const conversationId = selectedConversation?.id;

  const handleAddProductToConversation = useCallback(
    async (row) => {
      if (row.inCart || !row.productId || conversationId == null) return;
      const withPid = cartProducts.filter((p) => p.productId);
      if (withPid.length !== cartProducts.length) {
        // eslint-disable-next-line no-alert
        alert("Cada item do protocolo precisa estar vinculado a um produto do catálogo. Ajuste em Produtos.");
        return;
      }
      setCartSaving(true);
      try {
        const items = withPid.map((p) => ({
          productId: p.productId,
          quantity: p.quantidade,
          unitPrice: p.valor,
        }));
        items.push({
          productId: row.productId,
          quantity: 1,
          unitPrice: row.valor,
        });
        await putConversationCart(conversationId, items);
        if (typeof onRefetchConversationDetails === "function") {
          await onRefetchConversationDetails();
        }
      } catch (e) {
        // eslint-disable-next-line no-alert
        alert(e?.message || "Erro ao vincular produto ao protocolo.");
      } finally {
        setCartSaving(false);
      }
    },
    [cartProducts, conversationId, onRefetchConversationDetails]
  );

  const mentionOptions = useMemo(() => {
    const human = humanUsers.map((u) => ({ type: "user", id: u.id, label: u.nome || u.name || "", kind: "Humano" }));
    const chatbots = chatbotUsers.map((u) => ({ type: "user", id: u.id, label: u.nome || u.name || "", kind: "ChatBot" }));
    const agentes = (agents || []).map((a) => ({ type: "agent", id: a.id, label: a.name || a.nome || "", kind: "Agente de IA" }));
    const all = [...human, ...chatbots, ...agentes].filter((o) => o.label);
    if (!mentionQueryLower) return all;
    return all.filter((o) => (o.label || "").toLowerCase().includes(mentionQueryLower));
  }, [humanUsers, chatbotUsers, agents, mentionQueryLower]);

  /** Mensagem parece menção a colega (@nome) — confirmação antes de enviar ao cliente por canal externo. */
  const colleagueAtMentionIntent = useMemo(() => {
    const raw = messageText.trimStart();
    if (!raw.startsWith("@")) return false;
    const rest = raw.slice(1).trimStart();
    if (!rest) return false;
    const labels = [
      ...humanUsers.map((u) => u.nome || u.name),
      ...chatbotUsers.map((u) => u.nome || u.name),
      ...(agents || []).map((a) => a.name || a.nome),
    ]
      .filter((x) => x && String(x).trim())
      .map((x) => String(x).trim());
    for (const label of labels) {
      const lowLab = label.toLowerCase();
      const lowRest = rest.toLowerCase();
      if (lowRest.startsWith(`${lowLab} `) || lowRest === lowLab) return true;
      const firstWord = rest.split(/\s+/)[0]?.toLowerCase();
      const labFirst = label.split(/\s+/)[0]?.toLowerCase();
      if (firstWord && labFirst && firstWord === labFirst) return true;
    }
    return false;
  }, [messageText, humanUsers, chatbotUsers, agents]);

  const formatCartCurrency = (v) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));

  const buildCartProductSummary = (p) =>
    `${p.nome}\n${formatCartCurrency(p.valor)} un. · ${p.quantidade} un.\n${p.sku ? `SKU: ${p.sku}\n` : ""}`.trim();

  const handleSelectMention = (option) => {
    if (lastAt < 0) return;
    const newMessage = messageText.slice(0, lastAt + 1) + (option.label || "") + " ";
    setMessage(newMessage);
    setTransferTo({ type: option.type, id: option.id, label: option.label });
  };

  const handleSelectProductText = (p) => {
    if (lastHash < 0) return;
    setMessage(messageText.slice(0, lastHash) + buildCartProductSummary(p) + " ");
  };

  const handleSelectProductImage = (p) => {
    if (lastHash < 0 || !p.coverImageUrl || !isWhatsappAcceptedImageUrl(p.coverImageUrl)) return;
    if (selectedConversation?.id == null) return;
    setMessage(messageText.slice(0, lastHash).replace(/\s+$/, ""));
    window.dispatchEvent(
      new CustomEvent("xpro-chat-compose", {
        detail: {
          conversationId: Number(selectedConversation.id),
          imageUrl: p.coverImageUrl,
          appendText: p.nome || "",
        },
      })
    );
  };

  useEffect(() => {
    setMentionHighlightIndex(0);
  }, [mentionOptions.length, mentionQuery]);

  useEffect(() => {
    setProductHighlightIndex(0);
  }, [productOptions.length, productQuery]);

  const pickerOverlayOpen =
    (mentionPickerActive && mentionOptions.length > 0) || productPickerActive;

  // Position overlay above the input (no focus trap)
  useLayoutEffect(() => {
    if (pickerOverlayOpen && mentionAnchorRef.current) {
      const rect = mentionAnchorRef.current.getBoundingClientRect();
      const gap = 8;
      setMentionOverlayPosition({
        bottom: window.innerHeight - rect.top + gap,
        left: rect.left,
        width: Math.max(rect.width, 320),
      });
    } else {
      setMentionOverlayPosition(null);
    }
  }, [pickerOverlayOpen, mentionOptions.length]);

  // Teclado: @ (menções) ou # (produtos: protocolo + catálogo) — Enter = texto; Shift+Enter = foto (JPEG/PNG)
  useEffect(() => {
    const mentionMode = mentionPickerActive && mentionOptions.length > 0;
    const productMode = productPickerActive;
    if (!mentionMode && !productMode) return;

    const onKey = (e) => {
      if (mentionMode) {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          if (lastAt >= 0) setMessage((m) => (typeof m === "string" ? m : "").slice(0, lastAt));
          return;
        }
        if (e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          const opt = mentionOptions[mentionHighlightIndex];
          if (opt) handleSelectMention(opt);
          return;
        }
        if (e.key === "ArrowDown") {
          e.preventDefault();
          e.stopPropagation();
          setMentionHighlightIndex((i) => (i + 1) % mentionOptions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          e.preventDefault();
          e.stopPropagation();
          setMentionHighlightIndex((i) => (i - 1 + mentionOptions.length) % mentionOptions.length);
          return;
        }
        return;
      }

      if (productMode) {
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          if (lastHash >= 0) setMessage((m) => (typeof m === "string" ? m : "").slice(0, lastHash));
          return;
        }
        if (e.key === "Enter" && e.shiftKey) {
          if (productOptions.length === 0) return;
          const p = productOptions[productHighlightIndex];
          if (p?.coverImageUrl && isWhatsappAcceptedImageUrl(p.coverImageUrl)) {
            e.preventDefault();
            e.stopPropagation();
            handleSelectProductImage(p);
          }
          return;
        }
        if (e.key === "Enter") {
          if (productOptions.length === 0) return;
          e.preventDefault();
          e.stopPropagation();
          const p = productOptions[productHighlightIndex];
          if (p) handleSelectProductText(p);
          return;
        }
        if (e.key === "ArrowDown") {
          if (productOptions.length === 0) return;
          e.preventDefault();
          e.stopPropagation();
          setProductHighlightIndex((i) => (i + 1) % productOptions.length);
          return;
        }
        if (e.key === "ArrowUp") {
          if (productOptions.length === 0) return;
          e.preventDefault();
          e.stopPropagation();
          setProductHighlightIndex((i) => (i - 1 + productOptions.length) % productOptions.length);
          return;
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [
    mentionPickerActive,
    mentionOptions,
    mentionHighlightIndex,
    lastAt,
    productPickerActive,
    productOptions,
    productHighlightIndex,
    lastHash,
    messageText,
    selectedConversation?.id,
  ]);

  const currentChannel = getChannelById(channels, selectedChannel);
  const channelType =
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
      ? "WhatsApp"
      : String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)
        ? "Xpro"
        : getChannelTypeById(channels, selectedChannel);
  const channelName =
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
      ? "WhatsApp pessoal"
      : currentChannel?.nome || currentChannel?.name || null;
  const selectedChannelTipo =
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
      ? null
      : currentChannel?.tipo != null && currentChannel?.tipo !== ""
        ? Number(currentChannel.tipo)
        : null;

  const phoneSendBlocked = useMemo(() => {
    if (String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)) return false;
    if (String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)) return false;
    if (selectedConversation?.kind === "personal") return false;
    return (
      channelTypeRequiresClientPhone(channelType) && !hasClientPhoneRegistered(selectedConversation)
    );
  }, [selectedChannel, channelType, selectedConversation]);

  const locationBlockedWhatsappGroup = useMemo(() => {
    if (!isWhatsAppQrChannelTipo(selectedChannelTipo)) return false;
    if (channelType !== "WhatsApp" && channelType !== "App") return false;
    return isLikelyWhatsappGroupJid(selectedConversation?.celular);
  }, [channelType, selectedChannelTipo, selectedConversation?.celular]);

  useEffect(() => {
    if (selectedConversation?.kind === "personal") {
      setSelectedChannel(PERSONAL_WHATSAPP_CHANNEL_ID);
      return;
    }
    const initialChannel =
      sessionStorage.getItem(`selectedChannel${selectedConversation?.id}`) ||
      selectedConversation?.channelId;

    setSelectedChannel(initialChannel);
  }, [selectedConversation?.id, selectedConversation?.channelId, selectedConversation?.kind]);

  const onComposeChannelChangeRef = useRef(onComposeChannelChange);
  useEffect(() => {
    onComposeChannelChangeRef.current = onComposeChannelChange;
  }, [onComposeChannelChange]);

  useEffect(() => {
    if (typeof onComposeChannelChangeRef.current === "function") {
      onComposeChannelChangeRef.current(selectedChannel);
    }
  }, [selectedChannel]);

  // Ao apagar o texto do input, desvincular o template selecionado (evita enviar template ao enviar mensagem vazia/editada).
  useEffect(() => {
    if (!messageText.trim()) {
      setSelectedTemplate(null);
    }
  }, [messageText]);

  // Ao apagar o @ ou o nome do usuário do input, desvincular transferTo para não transferir se o usuário foi removido do texto.
  useEffect(() => {
    if (!transferTo) return;
    const lastAtPos = messageText.lastIndexOf("@");
    if (lastAtPos < 0) {
      setTransferTo(null);
      return;
    }
    const afterAt = messageText.slice(lastAtPos + 1);
    const label = transferTo.label || "";
    const stillMentioned = afterAt === label || afterAt === label + " " || afterAt.startsWith(label + " ");
    if (!stillMentioned) {
      setTransferTo(null);
    }
  }, [messageText, transferTo]);

  // Carrega sugestões (templates + respostas pré-definidas) ao mudar conversa, canal de envio ou tipo da solicitação.
  useEffect(() => {
    let isCancelled = false;

    async function loadSuggestions() {
      if (!selectedConversation?.id) return;
      if (selectedConversation?.kind === "personal") {
        if (!isCancelled) {
          setSuggestions({ templates: [], quickReplies: [] });
          setIsLoadingSuggestions(false);
          setSuggestionsError(null);
        }
        return;
      }
      if (channelType !== "WhatsApp") {
        if (!isCancelled) {
          setSuggestions({ templates: [], quickReplies: [] });
          setIsLoadingSuggestions(false);
          setSuggestionsError(null);
        }
        return;
      }

      try {
        setIsLoadingSuggestions(true);
        setSuggestionsError(null);
        const params = {
          originId: selectedChannel ?? selectedConversation?.channelId ?? undefined,
          typeId: selectedConversation?.typeId ?? undefined,
        };
        const data = await getConversationMessageSuggestions(selectedConversation.id, params);
        if (isCancelled) return;
        setSuggestions({
          templates: Array.isArray(data?.templates) ? data.templates : [],
          quickReplies: Array.isArray(data?.quickReplies) ? data.quickReplies : [],
          originId: data?.originId ?? null,
        });
      } catch (err) {
        if (isCancelled) return;
        console.error("Erro ao carregar sugestões de mensagens:", err);
        setSuggestionsError(err?.message || "Erro ao carregar sugestões de mensagens.");
      } finally {
        if (!isCancelled) {
          setIsLoadingSuggestions(false);
        }
      }
    }

    loadSuggestions();

    return () => {
      isCancelled = true;
    };
  }, [selectedConversation?.id, selectedConversation?.typeId, selectedConversation?.channelId, channelType, selectedChannel]);

  // Calcula se, de acordo com as regras de 24h do WhatsApp,
  // é recomendável iniciar a conversa usando template.
  const requiresTemplate = (() => {
    if (channelType !== "WhatsApp") return false;
    if (isWhatsAppQrChannelTipo(selectedChannelTipo)) return false;

    if (!Array.isArray(messages) || messages.length === 0) {
      // Conversa sem histórico: tipicamente é preciso usar template para iniciar.
      return true;
    }

    const clientMessages = messages.filter((msg) => {
      if (!msg) return false;
      const senderType = msg.senderType || msg.sender;
      if (!senderType) return false;
      const normalized = String(senderType).toLowerCase();
      return normalized === "client" || normalized === "cliente" || normalized === "user";
    });

    if (!clientMessages.length) return false;

    let lastClientDate = null;
    clientMessages.forEach((msg) => {
      const ts =
        msg.updatedAt ||
        msg.lastUpdate ||
        msg.timestamp ||
        msg.date;
      if (!ts) return;
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return;
      if (!lastClientDate || d > lastClientDate) {
        lastClientDate = d;
      }
    });

    if (!lastClientDate) return false;

    const diffMs = Date.now() - lastClientDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return diffHours >= 24;
  })();

  const composeSendBlocked = useMemo(() => {
    const personal =
      selectedConversation?.kind === "personal" ||
      String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID);
    if (channelType !== "WhatsApp" || personal) return false;
    const hasPayload = Boolean(messageText.trim()) || files.length > 0 || Boolean(audioURL);
    if (!hasPayload) return false;
    const templateRequiredForThisSend =
      requiresTemplate &&
      !selectedTemplate?.id &&
      (Boolean(messageText.trim()) || (files.length === 0 && !audioURL));
    if (templateRequiredForThisSend) return true;
    return false;
  }, [
    channelType,
    selectedConversation?.kind,
    selectedChannel,
    messageText,
    files.length,
    audioURL,
    requiresTemplate,
    selectedTemplate?.id,
  ]);

  /** Aviso compacto: só canal com tipo ≠ default, só para o responsável atual, até a 1ª mensagem externa enviada. */
  const showExternalSendBanner = useMemo(() => {
    if (selectedConversation?.kind === "personal") return false;
    if (String(selectedChannel) === String(INTERNAL_NOTE_CHANNEL_ID)) return false;
    if (String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)) return false;
    if (phoneSendBlocked) return false;
    if (!isClientOutboundChannelTipo(selectedChannelTipo)) return false;
    const myId = Number(userData?.id);
    const assigneeId = Number(
      selectedConversation?.userId ?? selectedConversation?.user_id ?? selectedConversation?.idUsuario
    );
    if (!Number.isFinite(myId) || myId <= 0) return false;
    if (!Number.isFinite(assigneeId) || assigneeId <= 0) return false;
    if (assigneeId !== myId) return false;
    if (hasMyExternalOutboundMessage(messages, myId)) return false;
    try {
      const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
      if (k && sessionStorage.getItem(k) === "1") return false;
    } catch (_) {
      /* ignore */
    }
    return true;
  }, [
    selectedChannel,
    selectedChannelTipo,
    selectedConversation?.id,
    selectedConversation?.kind,
    selectedConversation?.userId,
    selectedConversation?.user_id,
    selectedConversation?.idUsuario,
    userData?.id,
    messages,
    phoneSendBlocked,
  ]);

  const locationSendDisabledHelp = useMemo(() => {
    if (locationBlockedWhatsappGroup) return t("location.notSupportedWhatsappGroupQr");
    if (requiresTemplate) return t("input.locationOutside24h");
    if (phoneSendBlocked) return t("input.phoneRequiredSnackbar");
    return null;
  }, [locationBlockedWhatsappGroup, requiresTemplate, phoneSendBlocked, t]);

  useEffect(() => {
    if (locationConfirmOpen) setLocationConfirmMapFailed(false);
  }, [locationConfirmOpen]);

  const handleGenerateSuggestion = async () => {
    if (
      !selectedConversation?.id ||
      isGeneratingSuggestion ||
      !hasHistory
    )
      return;
    setIsGeneratingSuggestion(true);
    try {
      const extra = suggestionExtrasRef.current || {};
      const body = {
        draft: messageText.trim() || "",
        channelName,
        channelType,
        channelId: selectedChannel,
      };
      if (extra.focusProductIds?.length) body.focusProductIds = extra.focusProductIds;
      if (extra.focusLineItemIds?.length) body.focusLineItemIds = extra.focusLineItemIds;
      const response = await generateSuggestedReply(selectedConversation.id, body);
      suggestionExtrasRef.current = { focusProductIds: [], focusLineItemIds: [] };
      const suggestion =
        response?.suggestion || response?.data?.suggestion || "";
      if (suggestion) {
        setMessage(suggestion);
      }
    } catch (error) {
      // manter simples, seguindo padrão atual
      // eslint-disable-next-line no-alert
      alert(
        "Não foi possível gerar uma sugestão de resposta agora. Tente novamente em instantes."
      );
      // eslint-disable-next-line no-console
      console.error("Erro ao gerar sugestão de resposta com IA:", error);
    } finally {
      setIsGeneratingSuggestion(false);
    }
  };

  const renderChannelComponent = () => {
    switch (channelType) {
      case "WhatsApp":
        return (
          <MessageWhatsAppChannel
            message={messageText}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            isPersonalWhatsappThread={isPersonalWhatsappThread}
            formatOptions={formatOptions}
            startRecording={startRecording}
            openFilePicker={openFilePicker}
            onOpenProductPicker={openProductPickerFromMenu}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            suggestions={suggestions}
            isLoadingSuggestions={isLoadingSuggestions}
            suggestionsError={suggestionsError}
            requiresTemplate={requiresTemplate}
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
            externalPickerOpen={pickerOverlayOpen}
            sendBlockedMissingPhone={phoneSendBlocked}
            sendComposeBlocked={composeSendBlocked}
            onSendCurrentLocation={handleSendCurrentLocation}
            locationSending={locationSending}
            locationSendDisabled={requiresTemplate || phoneSendBlocked || locationBlockedWhatsappGroup}
            locationSendDisabledHelp={locationSendDisabledHelp}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            onRemoveAudio={handleRemoveAudio}
          />
        );
      case "Email":
        return (
          <MessageEmailChannel
            message={messageText}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            isRecording={isRecording}
            startRecording={startRecording}
            stopRecording={stopRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            isMobile={isMobile}
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            handleRemoveFile={handleRemoveFile}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            recipientEmail={selectedConversation?.email}
            messages={messages}
          />
        );
      case "Phone":
        return <MessagePhoneChannel
          selectedChannel={selectedChannel}
          setSelectedChannel={handleChannelChange}
          conversationId={selectedConversation?.id}
          originOptions={["computer", "(11) 91234-5678", "custom"]}
          userPhone="(11) 91234-5678" // Exemplo do telefone do usuário
          customerPhones={["(21) 99876-5432", "(31) 98765-4321"]} // Telefones do cliente
          origin={origin}
          setOrigin={setOrigin}
          destination={destination}
          setDestination={setDestination}
          handleSendMessage={handleSendMessage}
          isMobile={isMobile}
        /> ;
      case "Messenger":
      case "Instagram":
        return (
          <MessageMessengerChannel
            message={messageText}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
          />
        );
      case "SMS":
        return (
          <MessageSMSChannel
            message={messageText}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            sendBlockedMissingPhone={phoneSendBlocked}
          />
        );
      case "App":
        return (
          <MessageAppChannel
            message={messageText}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            startRecording={startRecording}
            openFilePicker={openFilePicker}
            onOpenProductPicker={openProductPickerFromMenu}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            sendBlockedMissingPhone={phoneSendBlocked}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            onRemoveAudio={handleRemoveAudio}
            {...(isWhatsAppQrChannelTipo(selectedChannelTipo)
              ? {
                  onSendCurrentLocation: handleSendCurrentLocation,
                  locationSending,
                  locationSendDisabled: phoneSendBlocked || locationBlockedWhatsappGroup,
                  locationSendDisabledHelp,
                }
              : {})}
          />
        );
      case "Wordpress":
        return (
          <MessageWordpressChannel
            message={messageText}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
          />
        );
      default:
        return (
          <MessageInputField
            message={messageText}
            setMessage={setMessage}
            selectedChannel={selectedChannel}
            channelType={channelType}
            setSelectedChannel={handleChannelChange}
            isPersonalWhatsappThread={isPersonalWhatsappThread}
            formatOptions={formatOptions}
            isRecording={isRecording}
            recordingDuration={recordingDuration}
            startRecording={startRecording}
            stopRecording={stopRecording}
            discardRecording={discardRecording}
            openFilePicker={openFilePicker}
            onOpenProductPicker={openProductPickerFromMenu}
            handleEmojiPickerOpen={handleEmojiPickerOpen}
            handleSendMessage={handleSendMessage}
            file={files[0]}
            files={files}
            audioURL={audioURL}
            onRemoveAudio={handleRemoveAudio}
            onGenerateSuggestion={handleGenerateSuggestion}
            isGeneratingSuggestion={isGeneratingSuggestion}
            hasHistory={hasHistory}
            sendBlockedMissingPhone={phoneSendBlocked}
          />
        );
    }
  };

  const handleChannelChange = (channelId) => {
    setSelectedChannel(channelId);
    sessionStorage.setItem(`selectedChannel${selectedConversation?.id}`, channelId);
  };

  const handleSendCurrentLocation = () => {
    if (
      String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID) &&
      channelTypeRequiresClientPhone(channelType) &&
      !hasClientPhoneRegistered(selectedConversation)
    ) {
      setPhoneBlockSnackbarOpen(true);
      return;
    }
    if (requiresTemplate) {
      return;
    }
    if (locationBlockedWhatsappGroup) {
      return;
    }
    if (!navigator.geolocation) {
      // eslint-disable-next-line no-alert
      alert(t("location.geolocationUnsupported"));
      return;
    }
    setLocationSending(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const latitude = pos.coords.latitude;
        const longitude = pos.coords.longitude;
        setLocationSending(false);
        setLocationDraft({ latitude, longitude });
        setLocationConfirmOpen(true);
      },
      (err) => {
        setLocationSending(false);
        const denied = err && err.code === err.PERMISSION_DENIED;
        // eslint-disable-next-line no-alert
        alert(denied ? t("location.geolocationDenied") : t("location.geolocationError"));
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60_000 }
    );
  };

  const handleCancelLocationConfirm = () => {
    setLocationConfirmOpen(false);
    setLocationDraft(null);
  };

  const handleConfirmLocationSend = () => {
    if (!locationDraft) return;
    if (typeof onSendMessage === "function") {
      onSendMessage({
        message: "",
        files: [],
        audioURL: null,
        channel: selectedChannel,
        channelType,
        location: {
          latitude: locationDraft.latitude,
          longitude: locationDraft.longitude,
        },
        ...(transferTo && { transferTo }),
      });
      if (String(selectedChannel) !== String(INTERNAL_NOTE_CHANNEL_ID)) {
        try {
          const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
          if (k) sessionStorage.setItem(k, "1");
        } catch (_) {
          /* ignore */
        }
      }
    }
    setLocationConfirmOpen(false);
    setLocationDraft(null);
  };

  const handleSendMessage = (emailOverrides = {}, meta = {}) => {
    const hasContent = messageText.trim() || files.length > 0 || audioURL;
    const payloadChannel = meta.overrideChannel ?? selectedChannel;
    const payloadChannelType =
      String(payloadChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID)
        ? "WhatsApp"
        : String(payloadChannel) === String(INTERNAL_NOTE_CHANNEL_ID)
          ? "Xpro"
          : getChannelTypeById(channels, payloadChannel);
    const personalWa =
      selectedConversation?.kind === "personal" ||
      String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID);
    const sendingInternal =
      String(payloadChannel) === String(INTERNAL_NOTE_CHANNEL_ID);

    if (
      hasContent &&
      !sendingInternal &&
      channelTypeRequiresClientPhone(payloadChannelType) &&
      !hasClientPhoneRegistered(selectedConversation)
    ) {
      setPhoneBlockSnackbarOpen(true);
      return;
    }

    if (
      hasContent &&
      channelType === "WhatsApp" &&
      !personalWa &&
      !sendingInternal
    ) {
      const templateRequiredForThisSend =
        requiresTemplate &&
        !selectedTemplate?.id &&
        (Boolean(messageText.trim()) || (files.length === 0 && !audioURL));
      if (templateRequiredForThisSend) {
        setComposeRuleKey("template");
        setComposeRuleSnackbarOpen(true);
        return;
      }
    }

    if (
      hasContent &&
      !meta.forceClientSend &&
      !sendingInternal &&
      colleagueAtMentionIntent
    ) {
      setMentionConfirmOpen(true);
      return;
    }

    if (hasContent) {
      const payload = {
        message: messageText.trim(),
        files: files.length ? [...files] : [],
        audioURL: audioURL || null,
        channel: payloadChannel,
        channelType: payloadChannelType,
        ...(transferTo && { transferTo }),
        ...(selectedTemplate?.id && { templateId: selectedTemplate.id }),
        ...(payloadChannelType === "Email" && {
          emailTo: emailOverrides.emailTo ?? selectedConversation?.email,
          ...(emailOverrides.emailSubject != null && { emailSubject: emailOverrides.emailSubject }),
          ...(emailOverrides.emailCc != null && { emailCc: emailOverrides.emailCc }),
        }),
      };
      if (typeof onSendMessage === "function") {
        onSendMessage(payload);
        if (!sendingInternal) {
          try {
            const k = EXTERNAL_CLIENT_BANNER_DONE_KEY(selectedConversation?.id);
            if (k) sessionStorage.setItem(k, "1");
          } catch (_) {
            /* ignore */
          }
        }
      } else {
        console.error("onSendMessage não é uma função válida.");
      }
      if (meta.overrideChannel != null) {
        handleChannelChange(meta.overrideChannel);
      }
      setMessage("");
      setFiles([]);
      setAudioURL(null);
      setTransferTo(null);
      setSelectedTemplate(null);
    }
  };

  const handleMentionSendToClient = () => {
    setMentionConfirmOpen(false);
    handleSendMessage({}, { forceClientSend: true });
  };

  const handleMentionSaveAsInternal = () => {
    setMentionConfirmOpen(false);
    handleSendMessage({}, {
      forceClientSend: true,
      overrideChannel: INTERNAL_NOTE_CHANNEL_ID,
    });
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // Resetar o input de arquivo.
      fileInputRef.current.click();
    }
  };

  /** Mesmo fluxo de digitar # no campo: abre o seletor de produtos (protocolo + catálogo). */
  const openProductPickerFromMenu = useCallback(() => {
    setMessage((m) => {
      const ms = typeof m === "string" ? m : "";
      const lh = ms.lastIndexOf("#");
      if (lh >= 0 && !/\s/.test(ms.slice(lh + 1))) {
        return ms;
      }
      const sep = ms.length && !/\s$/.test(ms) ? " " : "";
      return `${ms}${sep}#`;
    });
  }, []);

  const handleFileUploadInput = (e) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    if (selectedFiles.length) {
      handleFileUpload(selectedFiles);
    }
    e.target.value = "";
  };

  const handleFileUpload = (fileOrFiles) => {
    if (!fileOrFiles) return;
    const toAdd = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    setFiles((prev) => [...prev, ...toAdd]);
  };

  const handleRemoveFile = (index) => {
    if (typeof index === "number") {
      setFiles((prev) => prev.filter((_, i) => i !== index));
    } else {
      setFiles([]);
    }
  };

  // Ao remover o áudio, limpar também o anexo (files) para evitar que o preview de arquivo fique visível
  const handleRemoveAudio = () => {
    if (audioURL && audioURL.startsWith("blob:")) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioURL(null);
    setFiles([]);
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices) {
      alert("Gravação de áudio não é suportada neste navegador.");
      return;
    }
    try {
      if (!mp3RecorderRef.current) {
        mp3RecorderRef.current = new MicRecorder({ bitRate: 128 });
      }
      await mp3RecorderRef.current.start();
      setIsRecording(true);
      setRecordingDuration(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);
    } catch (e) {
      console.error("Erro ao iniciar gravação:", e);
      alert("Não foi possível acessar o microfone. Verifique as permissões.");
    }
  };

  const stopRecording = () => {
    discardRequestedRef.current = false;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        if (discardRequestedRef.current) {
          discardRequestedRef.current = false;
          setIsRecording(false);
          return;
        }
        const audioUrl = URL.createObjectURL(blob);
        setAudioURL(audioUrl);
        setIsRecording(false);
      })
      .catch((e) => {
        console.error("Erro ao finalizar gravação:", e);
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  };

  const discardRecording = () => {
    discardRequestedRef.current = true;
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    setRecordingDuration(0);
    if (!mp3RecorderRef.current) {
      setIsRecording(false);
      return;
    }
    mp3RecorderRef.current
      .stop()
      .getMp3()
      .then(([buffer, blob]) => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      })
      .catch(() => {
        discardRequestedRef.current = false;
        setIsRecording(false);
      });
  };

  const handleEmojiPickerOpen = (event) => setEmojiAnchorEl(event.currentTarget);
  const handleEmojiPickerClose = () => setEmojiAnchorEl(null);
  const handleSelectEmoji = (emoji) =>
    setMessage((prev) => `${typeof prev === "string" ? prev : ""}${emoji}`);

  const hasComposerPayload =
    Boolean(messageText.trim()) || files.length > 0 || Boolean(audioURL);
  // Botão principal (desktop): quando não há texto/anexo/áudio, mostra MIC para gravar;
  // quando há payload, mostra SEND. Durante a gravação não mostrar outro mic (evita ambiguidade).
  const canRecordOnDesktop = channelType !== "Phone" && channelType !== "Email";
  const showDesktopMicButton = canRecordOnDesktop && !hasComposerPayload && !isRecording;
  const isPersonalWhatsappThread =
    selectedConversation?.kind === "personal" ||
    String(selectedChannel) === String(PERSONAL_WHATSAPP_CHANNEL_ID);
  const sendAccent = isPersonalWhatsappThread ? "#128C7E" : null;
  const sendAccentHover = isPersonalWhatsappThread ? "#0f6f64" : null;
  /** E-mail/Telefone não embutem gravação na cápsula do canal; demais canais substituem só o campo de texto. */
  const voiceUiEmbeddedInChannel = channelType !== "Email" && channelType !== "Phone";

  return (
    <FileDropHandler onFileUpload={handleFileUpload}>
      {/* Respiro no rodapé (desktop também não pode ficar colado). */}
      <Box paddingBottom={isMobile ? 1 : 0.5}>
        {phoneSendBlocked && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t("input.phoneRequiredBanner")}
          </Alert>
        )}
        {showExternalSendBanner && (
          <Typography
            component="div"
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              px: 0.25,
              lineHeight: 1.25,
              color: "text.secondary",
            }}
          >
            {t("input.externalSendBanner", {
              channel: channelName || channelType || "",
            })}
          </Typography>
        )}
        <Box 
          display="flex"
          alignItems="center"
          gap={isMobile ? 0.5 : 1}
          flexWrap={isMobile ? "wrap" : "nowrap"} // Para evitar quebra de linha no desktop
          sx={{
            justifyContent: isMobile ? "space-between" : "flex-start", // Distribuição diferente entre mobile e desktop
          }}
        >
          {/* ChannelSelector agora é integrado por canal (layout padronizado). */}
          {/* {channelType === "Phone" ? (
            <MessagePhoneChannel
              selectedChannel={selectedChannel}
              handleChannelChange={handleChannelChange}
              originOptions={["computer", "(11) 91234-5678", "custom"]}
              userPhone="(11) 91234-5678" // Exemplo do telefone do usuário
              customerPhones={["(21) 99876-5432", "(31) 98765-4321"]} // Telefones do cliente
              origin={origin}
              setOrigin={setOrigin}
              destination={destination}
              setDestination={setDestination}
              handleSendMessage={handleSendMessage}
              isMobile={isMobile}
            />               
          ) : (<MessageInputField
              message={messageText}
              setMessage={setMessage}
              selectedChannel={selectedChannel}
              channelType={channelType}
              setSelectedChannel={handleChannelChange}
              formatOptions={formatOptions}
              isRecording={isRecording}
              startRecording={startRecording}
              stopRecording={stopRecording}
              openFilePicker={openFilePicker}
              handleEmojiPickerOpen={handleEmojiPickerOpen}
              handleSendMessage={handleSendMessage}
              file={files[0]}
              audioURL={audioURL}
          />)} */}
          <Box
            ref={mentionAnchorRef}
            sx={{
              flex: 1,
              minWidth: 0,
              width: "100%",
              minHeight: COMPOSER_ROW_HEIGHT_PX,
              display: "flex",
              alignItems: "stretch",
              transition: "opacity 0.2s ease",
            }}
          >
            {voiceUiEmbeddedInChannel ? (
              renderChannelComponent()
            ) : isRecording ? (
              <RecordingIndicator
                durationSeconds={recordingDuration}
                onStop={stopRecording}
                onDiscard={discardRecording}
              />
            ) : audioURL ? (
              <AudioPreview audioURL={audioURL} onRemove={handleRemoveAudio} />
            ) : (
              renderChannelComponent()
            )}
          </Box>
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            multiple
            onChange={handleFileUploadInput}
          />
          {(!isMobile && channelType !== "Phone" && channelType !== "Email" && !isExpanded) && (
            <Tooltip
              title={
                isRecording
                  ? t("input.sendAfterStopTooltip")
                  : showDesktopMicButton
                    ? t("input.recordVoiceAria")
                    : ""
              }
              enterDelay={400}
              disableHoverListener={!isRecording && !showDesktopMicButton}
            >
              <span>
                <IconButton
                  onClick={showDesktopMicButton ? startRecording : handleSendMessage}
                  disabled={
                    phoneSendBlocked ||
                    composeSendBlocked ||
                    (showDesktopMicButton ? false : !messageText.trim() && files.length === 0 && !audioURL)
                  }
                  aria-label={
                    showDesktopMicButton ? t("input.recordVoiceAria") : t("input.sendAria")
                  }
                  sx={{
                    backgroundColor: sendAccent || "primary.main",
                    color: "primary.contrastText",
                    borderRadius: "50%",
                    padding: "8px",
                    alignSelf: "center",
                    "&:hover": { backgroundColor: sendAccentHover || "primary.dark" },
                    "&.Mui-disabled": {
                      backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                          ? "action.disabledBackground"
                          : "action.disabledBackground",
                      color: "action.disabled",
                    },
                  }}
                >
                  {showDesktopMicButton ? <Mic /> : <Send />}
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
        {!isExpanded && files.length > 0 && (
          <Box sx={{ width: "100%", mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {files.map((file, index) => (
              <FilePreview
                key={`${file.name}-${file.size}-${index}`}
                file={file}
                onRemove={() => handleRemoveFile(index)}
              />
            ))}
          </Box>
        )}
        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={handleEmojiPickerClose}
          anchorOrigin={{ vertical: "top", horizontal: "left" }}
          transformOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{
            paper: { sx: { maxHeight: "min(400px, 70vh)", overflow: "auto" } },
          }}
        >
          <EmojiPicker onSelectEmoji={handleSelectEmoji} />
        </Popover>
        {/* Lista de menções: overlay em Portal acima do input (sem roubar foco) */}
        {mentionOverlayPosition &&
          createPortal(
            <Box
              role="listbox"
              tabIndex={-1}
              onMouseDown={(e) => e.preventDefault()}
              sx={{
                position: "fixed",
                bottom: mentionOverlayPosition.bottom,
                left: mentionOverlayPosition.left,
                width: mentionOverlayPosition.width,
                maxHeight: "min(340px, 55vh)",
                overflow: "auto",
                bgcolor: "background.paper",
                boxShadow: 2,
                borderRadius: 1,
                zIndex: 1300,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {mentionPickerActive && mentionOptions.length > 0
                ? mentionOptions.map((opt, idx) => (
                    <Box
                      key={`${opt.type}-${opt.id}-${idx}`}
                      component="div"
                      onClick={() => handleSelectMention(opt)}
                      onMouseDown={(e) => e.preventDefault()}
                      sx={{
                        px: 1.5,
                        py: 1,
                        cursor: "pointer",
                        bgcolor: idx === mentionHighlightIndex ? "action.selected" : "transparent",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography variant="body2" component="span">
                        {String(opt.label ?? "")}
                      </Typography>
                      {opt.kind ? (
                        <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 1 }}>
                          ({String(opt.kind)})
                        </Typography>
                      ) : null}
                    </Box>
                  ))
                : productPickerActive
                  ? productOptions.length === 0
                    ? (
                        <Box
                          sx={{
                            px: 1.5,
                            py: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          {catalogLoading && catalogEnabled ? <CircularProgress size={22} /> : null}
                          <Typography variant="body2" color="text.secondary" textAlign="center">
                            {!projectId
                              ? "Sem projeto na solicitação: só é possível listar produtos já vinculados ao protocolo. Defina o projeto para buscar o catálogo completo."
                              : catalogLoading && catalogEnabled
                                ? "Carregando catálogo…"
                                : "Nenhum produto corresponde à busca."}
                          </Typography>
                        </Box>
                      )
                    : productOptions.map((p, idx) => {
                        const imgOk = Boolean(p.coverImageUrl && isWhatsappAcceptedImageUrl(p.coverImageUrl));
                        return (
                          <Box
                            key={p.rowKey}
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            sx={{
                              px: 0.5,
                              py: 0.25,
                              borderLeft: p.inCart ? 3 : 0,
                              borderColor: p.inCart ? "primary.main" : "transparent",
                              bgcolor: idx === productHighlightIndex ? "action.selected" : "transparent",
                              "&:hover": { bgcolor: "action.hover" },
                            }}
                          >
                            {p.inCart ? (
                              <Chip
                                size="small"
                                label="Protocolo"
                                color="primary"
                                variant="outlined"
                                sx={{ height: 22, flexShrink: 0, "& .MuiChip-label": { px: 0.75 } }}
                              />
                            ) : null}
                            <Box
                              component="div"
                              flex={1}
                              minWidth={0}
                              onClick={() => handleSelectProductText(p)}
                              onMouseDown={(e) => e.preventDefault()}
                              sx={{ cursor: "pointer", py: 0.5, pl: p.inCart ? 0 : 0.5 }}
                            >
                              <Typography variant="body2" noWrap>
                                {p.nome}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" component="span">
                                (
                                {formatCartCurrency(p.valor)} · {p.quantidade} un.
                                {p.sku ? ` · SKU ${p.sku}` : ""}
                                {!p.inCart ? " · Catálogo" : ""})
                              </Typography>
                            </Box>
                            {!p.inCart && p.productId ? (
                              <Tooltip title="Incluir no protocolo (carrinho)">
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={cartSaving}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleAddProductToConversation(p);
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    {cartSaving ? (
                                      <CircularProgress size={16} color="inherit" />
                                    ) : (
                                      <AddShoppingCart fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                            {p.coverImageUrl ? (
                              <Tooltip
                                title={
                                  imgOk
                                    ? "Carregar foto no envio (Shift+Enter na lista)"
                                    : "Imagem em formato não aceito pelo WhatsApp (JPEG/PNG). Atualize no cadastro do produto."
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color={imgOk ? "secondary" : "error"}
                                    disabled={!imgOk}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSelectProductImage(p);
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                  >
                                    <ImageIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : null}
                          </Box>
                        );
                      })
                  : null}
            </Box>,
            document.body
          )}
        <Snackbar
          open={phoneBlockSnackbarOpen}
          autoHideDuration={6000}
          onClose={() => setPhoneBlockSnackbarOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setPhoneBlockSnackbarOpen(false)}
            severity="warning"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {t("input.phoneRequiredSnackbar")}
          </Alert>
        </Snackbar>
        <Snackbar
          open={composeRuleSnackbarOpen}
          autoHideDuration={7000}
          onClose={() => {
            setComposeRuleSnackbarOpen(false);
            setComposeRuleKey(null);
          }}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => {
              setComposeRuleSnackbarOpen(false);
              setComposeRuleKey(null);
            }}
            severity="warning"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {composeRuleKey === "caption"
              ? t("input.composeMediaNeedsCaption")
              : t("input.composeRequiresTemplate")}
          </Alert>
        </Snackbar>
        <Dialog open={mentionConfirmOpen} onClose={() => setMentionConfirmOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>{t("input.mentionConfirmTitle")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {t("input.mentionConfirmBody")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
            <Button onClick={() => setMentionConfirmOpen(false)}>{t("input.mentionConfirmCancel")}</Button>
            <Button variant="outlined" onClick={handleMentionSaveAsInternal}>
              {t("input.mentionConfirmInternal")}
            </Button>
            <Button variant="contained" onClick={handleMentionSendToClient}>
              {t("input.mentionConfirmClient")}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={locationConfirmOpen}
          onClose={handleCancelLocationConfirm}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t("location.confirmTitle")}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t("location.approximateHint")}
            </Typography>
            {locationDraft &&
              (() => {
                const preview = buildStaticMapPreviewUrl(
                  locationDraft.latitude,
                  locationDraft.longitude,
                  { width: 560, height: 220 }
                );
                if (!preview) return null;
                if (locationConfirmMapFailed) {
                  return (
                    <Box
                      sx={{
                        width: "100%",
                        height: 160,
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: "action.hover",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <PlaceRounded color="error" sx={{ fontSize: 48, opacity: 0.9 }} />
                    </Box>
                  );
                }
                return (
                  <Box
                    component="img"
                    src={preview}
                    alt=""
                    referrerPolicy="no-referrer"
                    onError={() => setLocationConfirmMapFailed(true)}
                    sx={{
                      width: "100%",
                      height: 160,
                      objectFit: "cover",
                      borderRadius: 1,
                      display: "block",
                      mb: 1,
                      bgcolor: "action.hover",
                    }}
                  />
                );
              })()}
            {locationDraft && (
              <Typography variant="caption" color="text.secondary" component="div">
                {Number(locationDraft.latitude).toFixed(5)},{" "}
                {Number(locationDraft.longitude).toFixed(5)}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelLocationConfirm}>{t("location.confirmCancel")}</Button>
            <Button variant="contained" onClick={handleConfirmLocationSend}>
              {t("location.confirmSend")}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </FileDropHandler>
  );
};

export default MessageInput;
